import { useState, useEffect, useCallback } from 'react';
import {
  Location,
  SubLocation,
  CreateLocationPayload,
  UpdateLocationPayload,
  CreateSubLocationPayload,
  UpdateSubLocationPayload,
  ViewMode,
} from '../types/location';
import {
  getAllLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  getSubLocationsByLocation,
  createSubLocation,
  updateSubLocation,
  deleteSubLocation,
} from '../services/locationService';
import { useAlert } from './useAlert';
import { useAdminGuard } from './useAdminGuard';
import { extractApiError } from '../utils/errorUtils';
import { PaginationParams } from '../types/pagination';

type TabType = 'locations' | 'sublocations';

const DEFAULT_LIMIT = 20;

export function useLocations() {
  const { isAdmin } = useAdminGuard();
  const { alert, showAlert, hideAlert } = useAlert();
  const [activeTab, setActiveTab] = useState<TabType>('locations');
  const [locations, setLocations] = useState<Location[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [subLocations, setSubLocations] = useState<SubLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isSubLocationModalOpen, setIsSubLocationModalOpen] = useState(false);
  const [editingSubLocation, setEditingSubLocation] = useState<SubLocation | null>(null);

  const fetchLocations = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    hideAlert();
    try {
      const params: PaginationParams = { page, limit, search: search || undefined, sortBy, sortOrder };
      const result = await getAllLocations(params);
      setLocations(result.items);
      setTotal(result.total);
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to load locations.'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, page, limit, search, sortBy, sortOrder, hideAlert, showAlert]);

  const fetchSubLocations = useCallback(async (locationId: number) => {
    setIsLoading(true);
    hideAlert();
    try {
      const fetchedSubLocations = await getSubLocationsByLocation(locationId);
      setSubLocations(fetchedSubLocations);
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to load sublocations.'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [hideAlert, showAlert]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchLocations();
  }, [isAdmin, fetchLocations]);

  useEffect(() => {
    if (activeTab === 'sublocations' && selectedLocationId) {
      fetchSubLocations(selectedLocationId);
    } else if (activeTab === 'sublocations' && !selectedLocationId && locations.length > 0) {
      setSelectedLocationId(locations[0].id);
    }
  }, [activeTab, selectedLocationId, locations, fetchSubLocations]);

  const handleAddLocation = useCallback(() => {
    setEditingLocation(null);
    setIsLocationModalOpen(true);
    hideAlert();
  }, [hideAlert]);

  const handleEditLocation = useCallback((location: Location) => {
    setEditingLocation(location);
    setIsLocationModalOpen(true);
    hideAlert();
  }, [hideAlert]);

  const handleLocationFormSubmit = useCallback(async (formData: CreateLocationPayload | UpdateLocationPayload) => {
    hideAlert();
    setIsSubmitting(true);
    try {
      if (editingLocation) {
        const response = await updateLocation(formData as UpdateLocationPayload);
        setLocations((prev) => prev.map((l) => (l.id === editingLocation.id ? response.location : l)));
        showAlert(response.message || 'Location updated successfully!', 'success');
      } else {
        const response = await createLocation(formData as CreateLocationPayload);
        setLocations((prev) => [response.location, ...prev]);
        showAlert(response.message || 'Location created successfully!', 'success');
      }
      setIsLocationModalOpen(false);
      setEditingLocation(null);
    } catch (err) {
      showAlert(extractApiError(err, editingLocation ? 'Failed to update location.' : 'Failed to create location.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [editingLocation, hideAlert, showAlert]);

  const handleAddSubLocation = useCallback(() => {
    if (!selectedLocationId) {
      showAlert('Please select a location first.', 'error');
      return;
    }
    setEditingSubLocation(null);
    setIsSubLocationModalOpen(true);
    hideAlert();
  }, [selectedLocationId, hideAlert, showAlert]);

  const handleEditSubLocation = useCallback((subLocation: SubLocation) => {
    setEditingSubLocation(subLocation);
    setIsSubLocationModalOpen(true);
    hideAlert();
  }, [hideAlert]);

  const handleSubLocationFormSubmit = useCallback(async (formData: CreateSubLocationPayload | UpdateSubLocationPayload) => {
    hideAlert();
    setIsSubmitting(true);
    try {
      if (editingSubLocation) {
        const response = await updateSubLocation(formData as UpdateSubLocationPayload);
        setSubLocations((prev) => prev.map((sl) => (sl.id === editingSubLocation.id ? response.sublocation : sl)));
        showAlert(response.message || 'SubLocation updated successfully!', 'success');
      } else {
        const response = await createSubLocation(formData as CreateSubLocationPayload);
        setSubLocations((prev) => [response.sublocation, ...prev]);
        showAlert(response.message || 'SubLocation created successfully!', 'success');
      }
      setIsSubLocationModalOpen(false);
      setEditingSubLocation(null);
    } catch (err) {
      showAlert(extractApiError(err, editingSubLocation ? 'Failed to update sublocation.' : 'Failed to create sublocation.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [editingSubLocation, hideAlert, showAlert]);

  const handleViewSubLocations = useCallback((locationId: number) => {
    setSelectedLocationId(locationId);
    setActiveTab('sublocations');
  }, []);

  const handleDeleteLocation = useCallback(async (locationId: number) => {
    if (!window.confirm('Are you sure you want to delete this location? This will also delete all associated sublocations.')) return;
    hideAlert();
    try {
      await deleteLocation(locationId);
      setLocations((prev) => prev.filter((l) => l.id !== locationId));
      setTotal((t) => Math.max(0, t - 1));
      if (selectedLocationId === locationId) {
        setSelectedLocationId(null);
        setSubLocations([]);
      }
      showAlert('Location deleted successfully!', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to delete location.'), 'error');
    }
  }, [selectedLocationId, hideAlert, showAlert]);

  const handleDeleteSubLocation = useCallback(async (subLocationId: number) => {
    if (!window.confirm('Are you sure you want to delete this sublocation? This might affect stores using it.')) return;
    hideAlert();
    try {
      await deleteSubLocation(subLocationId);
      setSubLocations((prev) => prev.filter((sl) => sl.id !== subLocationId));
      showAlert('SubLocation deleted successfully!', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to delete sublocation.'), 'error');
    }
  }, [hideAlert, showAlert]);

  const closeLocationModal = useCallback(() => {
    setIsLocationModalOpen(false);
    setEditingLocation(null);
    hideAlert();
  }, [hideAlert]);

  const closeSubLocationModal = useCallback(() => {
    setIsSubLocationModalOpen(false);
    setEditingSubLocation(null);
    hideAlert();
  }, [hideAlert]);

  const totalPages = Math.ceil(total / limit) || 1;

  return {
    isAdmin,
    activeTab,
    setActiveTab,
    locations,
    subLocations,
    selectedLocationId,
    setSelectedLocationId,
    isLoading,
    isSubmitting,
    viewMode,
    setViewMode,
    isLocationModalOpen,
    isSubLocationModalOpen,
    editingLocation,
    editingSubLocation,
    alert,
    hideAlert,
    handleAddLocation,
    handleEditLocation,
    handleLocationFormSubmit,
    handleAddSubLocation,
    handleEditSubLocation,
    handleSubLocationFormSubmit,
    handleViewSubLocations,
    handleDeleteLocation,
    handleDeleteSubLocation,
    closeLocationModal,
    closeSubLocationModal,
    pagination: {
      page,
      limit,
      total,
      totalPages,
      setPage,
      setLimit,
      setSearch,
      setSortBy,
      setSortOrder,
      search,
      sortBy,
      sortOrder,
    },
  };
}
